import { useCallback, useEffect, useRef, useState } from 'react'

import { useStaticData } from '@mc/static-data-react'

import { setGoogleOneTapPageLoadCookie } from './googleOneTapCookie'
import { useExperiment } from './useExperiment'

const DELAY = 6000 // 6 seconds
const STORAGE_GOOGLE_FLYOUT_BANNED_KEY = 'pen-3951-v2-google-flyout-banned'
const STORAGE_CUSTOM_MODAL_BANNED_KEY = 'pen-3951-v2-custom-modal-banned'

export const useUserInteractions = () => {
  const [timerElapsed, setTimerElapsed] = useState(false)
  const [userScrolled, setUserScrolled] = useState(false)
  const timerRef: {
    current: ReturnType<typeof setTimeout> | undefined
  } = useRef()
  const [interacted, setInteracted] = useState<boolean>(false)
  const { isEnabled } = useExperiment()

  const { googleOneTapPageLoadCookie } = useStaticData<{
    googleOneTapPageLoadCookie: number
  }>() // defined in _app.page.js pageProps

  const userDidScroll = useCallback(() => {
    setUserScrolled(true)
  }, [setUserScrolled])

  const setUserInteractions = useCallback(() => {
    if (typeof window === 'undefined') return

    window.addEventListener('scroll', userDidScroll)
  }, [userDidScroll])

  const handleActions = useCallback(() => {
    if (typeof window === 'undefined') return

    // show the modal after 2nd page load
    if (isEnabled && !googleOneTapPageLoadCookie) {
      setGoogleOneTapPageLoadCookie()
      window.removeEventListener('scroll', userDidScroll)
      return
    }

    if (!timerElapsed || !userScrolled) return

    setInteracted(true)
    window.removeEventListener('scroll', userDidScroll)
  }, [
    timerElapsed,
    userScrolled,
    userDidScroll,
    isEnabled,
    googleOneTapPageLoadCookie,
  ])

  const ban = useCallback((modal: 'google-flyout' | 'pop-up') => {
    if (typeof window === 'undefined') return

    if (modal === 'google-flyout') {
      window.localStorage.setItem(STORAGE_GOOGLE_FLYOUT_BANNED_KEY, 'true')
    } else {
      window.localStorage.setItem(STORAGE_CUSTOM_MODAL_BANNED_KEY, 'true')
    }
  }, [])

  const isModalBanned = useCallback((modal?: 'google-flyout' | 'pop-up') => {
    if (typeof window === 'undefined') return false

    const googleFlyout =
      window.localStorage.getItem(STORAGE_GOOGLE_FLYOUT_BANNED_KEY) === 'true'
    const customPopup =
      window.localStorage.getItem(STORAGE_CUSTOM_MODAL_BANNED_KEY) === 'true'

    if (modal === 'google-flyout') {
      return googleFlyout
    }

    if (modal === 'pop-up') {
      return customPopup
    }

    return googleFlyout && customPopup
  }, [])

  useEffect(() => {
    if (isModalBanned()) return () => {}

    timerRef.current = setTimeout(() => {
      setTimerElapsed(true)
    }, DELAY)

    setUserInteractions()
    handleActions()

    return () => {
      clearTimeout(timerRef.current)
    }
  }, [setUserInteractions, handleActions, isModalBanned])

  return {
    interacted,
    ban,
    isModalBanned,
  }
}
